import React, { useState } from "react";
import { toast } from "sonner";
import { Send, Mail, MapPin } from "lucide-react";
import { api } from "../lib/api";
import SectionHeader from "../components/SectionHeader";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", website: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill all required fields");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. We'll be in touch soon.");
      setForm({ name: "", email: "", subject: "", message: "", website: "" });
    } catch (err) {
      const msg = err.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please try again");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="contact-page">
      <SectionHeader eyebrow="Contact" title="Get in Touch" subtitle="For press, distribution, casting, and general enquiries — send us a message." />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5 space-y-8">
          <div>
            <div className="font-cinzel text-[0.65rem] tracking-[0.4em] text-[color:var(--jk-gold)] uppercase mb-2">Email</div>
            <a href="mailto:jekepictures@gmail.com" className="flex items-center gap-3 text-white hover:text-[color:var(--jk-gold)] transition-colors" data-testid="contact-email">
              <Mail size={18} /> jekepictures@gmail.com
            </a>
          </div>
          <div>
            <div className="font-cinzel text-[0.65rem] tracking-[0.4em] text-[color:var(--jk-gold)] uppercase mb-2">Studio</div>
            <div className="flex items-center gap-3 text-white">
              <MapPin size={18} /> India
            </div>
          </div>
          <div className="p-6 border border-white/5">
            <div className="font-cinzel text-[0.65rem] tracking-[0.4em] text-[color:var(--jk-gold)] uppercase mb-3">Press & Distribution</div>
            <p className="text-sm text-[color:var(--jk-muted)] leading-relaxed">
              For press releases, distribution rights, and screening enquiries, kindly include your organisation, region, and timeline in your message.
            </p>
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-7 space-y-5" data-testid="contact-form">
          {/* Honeypot */}
          <input type="text" name="website" value={form.website} onChange={(e) => setForm({...form, website: e.target.value})} className="hidden" tabIndex="-1" autoComplete="off" aria-hidden="true" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-[0.65rem] tracking-[0.3em] font-cinzel text-[color:var(--jk-gold)] uppercase mb-2">Name *</label>
              <input type="text" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="jk-input" data-testid="contact-name" />
            </div>
            <div>
              <label className="block text-[0.65rem] tracking-[0.3em] font-cinzel text-[color:var(--jk-gold)] uppercase mb-2">Email *</label>
              <input type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="jk-input" data-testid="contact-email-field" />
            </div>
          </div>
          <div>
            <label className="block text-[0.65rem] tracking-[0.3em] font-cinzel text-[color:var(--jk-gold)] uppercase mb-2">Subject *</label>
            <input type="text" required value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})} className="jk-input" data-testid="contact-subject" />
          </div>
          <div>
            <label className="block text-[0.65rem] tracking-[0.3em] font-cinzel text-[color:var(--jk-gold)] uppercase mb-2">Message *</label>
            <textarea required rows={6} value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} className="jk-input resize-none" data-testid="contact-message" />
          </div>
          <button type="submit" disabled={loading} className="jk-btn jk-btn-solid disabled:opacity-50" data-testid="contact-submit-btn">
            {loading ? "SENDING..." : <>SEND MESSAGE <Send size={14} /></>}
          </button>
        </form>
      </div>
    </div>
  );
}
